import axios, { type AxiosResponse } from 'axios';
import { getApiBaseUrl } from './apiConfig';
import authService from './authService';

/**
 * Service provider = collaborator entity (promoter, photographer, sound
 * engineer, etc.). Model mirrors Performer's identity fields with
 * `specialty` replacing `genres` as the free-text role descriptor.
 */
export interface ServiceProviderRecord {
    id: string;
    name: string;
    specialty: string | null;
    bio: string | null;
    profilePicture: string | null;
    socialLinks: Record<string, any> | null;
    customLinks: Array<Record<string, any>> | null;
    /** Off-platform merch store (issue #43), validated http(s) by the server. */
    merchUrl?: string | null;
    isPublic: boolean;
    isActive: boolean;
    managedEntityId: string | null;
    parentEntityId: string | null;
    createdAt?: string;
    updatedAt?: string;
    upcomingEvents?: any[];
    /** Set on `scope=network` results. True when the row belongs to
     *  another workspace (vs being a local stub or your own entity). */
    isLinked?: boolean;
    /** Admin-approved blue-check (verificationStatus === 'verified'). Distinct
     *  from isLinked/managedEntityId — a real linked provider can be unverified. */
    isVerified?: boolean;
    /** Set on `scope=network` results. True when an outgoing
     *  EntityConnection (pending or accepted) exists from your workspace
     *  to this row's workspace. Drives the EntityProfileCard "Collab" button. */
    isFollowing?: boolean;
    /** Set on `scope=network` results. Direction-agnostic — matches
     *  whichever of pending/accepted exists between the two workspaces. */
    connectionStatus?: 'pending' | 'accepted';
}

const API_BASE = `${getApiBaseUrl()}/api/service-providers`;
const withAuth = () => ({ headers: authService.getAuthHeader() });

export const serviceProviderApi = {
    /** Public — loads the service provider by id for the profile page. */
    async getServiceProvider(id: string): Promise<ServiceProviderRecord> {
        const res: AxiosResponse<ServiceProviderRecord> = await axios.get(`${API_BASE}/${id}`);
        return res.data;
    },

    /** List/search service providers. Mirrors `performerApi.list` so generic
     *  entity-search UIs can call this without per-type branching. */
    async list(params: {
        managedEntityId?: string;
        scope?: 'global' | 'network' | 'local';
        searchTerm?: string;
        isActive?: boolean;
        limit?: number;
        offset?: number;
    } = {}): Promise<ServiceProviderRecord[]> {
        const res: AxiosResponse<ServiceProviderRecord[]> = await axios.get(API_BASE, {
            params,
            ...withAuth(),
        });
        return res.data;
    },

    /** Create a parent-owned service-provider stub (e.g. an off-platform
     *  collaborator the workspace wants to invite to an event). Mirrors
     *  `performerApi.createLocal`. */
    async createLocal(input: {
        name: string;
        specialty?: string;
        parentEntityId: string;
    }): Promise<ServiceProviderRecord> {
        const res: AxiosResponse<ServiceProviderRecord> = await axios.post(
            `${API_BASE}/local`,
            input,
            withAuth(),
        );
        return res.data;
    },

    /** Update mutable profile fields. Admin uses this for the rename
     *  / edit-profile flows on the management detail page. */
    async update(id: string, patch: Partial<ServiceProviderRecord>): Promise<ServiceProviderRecord> {
        const res: AxiosResponse<ServiceProviderRecord> = await axios.put(
            `${API_BASE}/${id}`,
            patch,
            withAuth(),
        );
        return res.data;
    },
};

export default serviceProviderApi;
